import React, { useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div<{ type: 'success' | 'error' }>`
  background: ${props => props.type === 'success' ? '#fff' : '#fff'};
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  position: relative;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const Icon = styled.div<{ type: 'success' | 'error' }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.type === 'success' 
    ? 'rgba(76, 175, 80, 0.1)' 
    : 'rgba(244, 67, 54, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.type === 'success' ? '#2e7d32' : '#c62828'};
  font-family: 'Poppins', sans-serif;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  margin-top: 1.5rem;
  padding: 0.75rem 2rem;
  background: var(--accent-orange);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 150, 0, 0.3);
  }
`;

interface MessageModalProps {
  isOpen: boolean;
  type: 'success' | 'error';
  message: string;
  onClose: () => void;
  autoCloseDelay?: number; // Auto-close after this many milliseconds (0 = no auto-close)
}

const MessageModal: React.FC<MessageModalProps> = ({
  isOpen,
  type,
  message,
  onClose,
  autoCloseDelay = 3000
}) => {
  useEffect(() => {
    if (isOpen && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDelay, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal type={type} onClick={(e) => e.stopPropagation()}>
        <Icon type={type}>
          {type === 'success' ? '✓' : '✕'}
        </Icon>
        <Message type={type}>{message}</Message>
        <CloseButton onClick={onClose}>OK</CloseButton>
      </Modal>
    </Overlay>
  );
};

export default MessageModal;


